import { createSignal, createMemo, For, Show, onMount } from "solid-js";
import { useParams, A } from "@solidjs/router";
import { useApi, isOk } from "../api";
import Text from "../components/Text";
import type { PlayerHistory, OnlineSession, PollWindow } from "../services/onlinePlayersApi";
import { formatDateTime } from "../services/dateFormat";
import styles from "./OnlinePlayerHistory.module.css";

interface SessionEntry {
    kind: "session";
    onlineAt: number;
    offlineAt: number | null;
}

interface GapEntry {
    kind: "gap";
    from: number;
    to: number;
}

type TimelineEntry = SessionEntry | GapEntry;

function toMs(iso: string | null): number | null {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    return isFinite(t) ? t : null;
}

function formatAbs(ms: number): string {
    return formatDateTime(ms);
}

function formatDuration(start: number, end: number | null): string {
    const e = end ?? Date.now();
    const total = Math.max(0, Math.floor((e - start) / 1000));
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
}

function buildTimeline(sessions: OnlineSession[], windows: PollWindow[]): TimelineEntry[] {
    const sortedSessions = sessions
        .map(s => ({ onlineAt: toMs(s.onlineAt)!, offlineAt: toMs(s.offlineAt) }))
        .filter(s => s.onlineAt != null)
        .sort((a, b) => a.onlineAt - b.onlineAt);

    if (sortedSessions.length === 0) return [];

    const windowsMs = windows
        .map(w => ({ from: toMs(w.startedAt)!, to: toMs(w.endedAt) ?? Date.now() }))
        .filter(w => w.from != null)
        .sort((a, b) => a.from - b.from);

    const merged: { from: number; to: number }[] = [];
    for (const w of windowsMs) {
        const last = merged[merged.length - 1];
        if (last && w.from <= last.to) {
            last.to = Math.max(last.to, w.to);
        } else {
            merged.push({ from: w.from, to: w.to });
        }
    }

    const earliest = sortedSessions[0].onlineAt;
    const latest = sortedSessions[sortedSessions.length - 1].offlineAt ?? Date.now();

    const entries: TimelineEntry[] = [];
    let cursor = earliest;

    for (const session of sortedSessions) {
        if (session.onlineAt > cursor) {
            const covered = isCovered(merged, cursor, session.onlineAt);
            if (!covered) {
                entries.push({ kind: "gap", from: cursor, to: session.onlineAt });
            }
        }
        entries.push({ kind: "session", onlineAt: session.onlineAt, offlineAt: session.offlineAt });
        cursor = session.offlineAt ?? Date.now();
    }

    if (cursor < latest && !isCovered(merged, cursor, latest)) {
        entries.push({ kind: "gap", from: cursor, to: latest });
    }

    return entries.reverse();
}

function isCovered(windows: { from: number; to: number }[], from: number, to: number): boolean {
    let cursor = from;
    for (const w of windows) {
        if (w.to <= cursor) continue;
        if (w.from > cursor) return false;
        cursor = w.to;
        if (cursor >= to) return true;
    }
    return cursor >= to;
}

export default function OnlinePlayerHistory() {
    const params = useParams<{ domain: string; name: string }>();
    const api = useApi();
    const [history, setHistory] = createSignal<PlayerHistory | null>(null);
    const [loading, setLoading] = createSignal(true);
    const [notFound, setNotFound] = createSignal(false);

    onMount(async () => {
        const res = await api.onlinePlayers.getPlayerHistory(encodeURIComponent(params.domain), encodeURIComponent(params.name));
        if (isOk(res.data)) {
            setHistory(res.data.value as PlayerHistory);
        } else if (res.data?.status === 404) {
            setNotFound(true);
        }
        setLoading(false);
    });

    const timeline = createMemo<TimelineEntry[]>(() => {
        const h = history();
        if (!h) return [];
        return buildTimeline(h.sessions, h.pollWindows);
    });

    const totalOnline = createMemo<number>(() => {
        const h = history();
        if (!h) return 0;
        let total = 0;
        for (const s of h.sessions) {
            const start = toMs(s.onlineAt);
            if (start == null) continue;
            const end = toMs(s.offlineAt) ?? Date.now();
            total += Math.max(0, end - start);
        }
        return total;
    });

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <A href="/online-players" class={styles.backLink}>
                    <i class="fa-solid fa-arrow-left" /> <Text val="ONLINE_PLAYERS_BACK" />
                </A>
                <h1>{decodeURIComponent(params.name)}</h1>
                <span class={styles.domainBadge}>{decodeURIComponent(params.domain)}</span>
            </div>

            <Show when={!loading()} fallback={
                <div class={styles.skeleton}>
                    <div class={styles.skeletonRow} />
                    <div class={styles.skeletonRow} />
                    <div class={styles.skeletonRow} />
                </div>
            }>
                <Show when={!notFound() && history()} fallback={
                    <div class={styles.empty}>
                        <i class="fa-solid fa-user-slash" />
                        <p><Text val="ONLINE_PLAYERS_HISTORY_EMPTY" /></p>
                    </div>
                }>
                    <div class={`${styles.summary} fcard`}>
                        <div class={styles.summaryItem}>
                            <span class={styles.summaryLabel}><Text val="ONLINE_PLAYERS_STATUS" /></span>
                            <span class={`${styles.statusPill} ${history()!.currentlyOnline ? styles.statusOnline : styles.statusOffline}`}>
                                {history()!.currentlyOnline ? "ONLINE" : "OFFLINE"}
                            </span>
                        </div>
                        <div class={styles.summaryItem}>
                            <span class={styles.summaryLabel}><Text val="ONLINE_PLAYERS_FIRST_SEEN" /></span>
                            <span class={styles.summaryValue}>{history()!.firstSeen ? formatAbs(toMs(history()!.firstSeen)!) : "-"}</span>
                        </div>
                        <div class={styles.summaryItem}>
                            <span class={styles.summaryLabel}><Text val="ONLINE_PLAYERS_LAST_SEEN" /></span>
                            <span class={styles.summaryValue}>{history()!.lastSeen ? formatAbs(toMs(history()!.lastSeen)!) : "-"}</span>
                        </div>
                        <div class={styles.summaryItem}>
                            <span class={styles.summaryLabel}><Text val="ONLINE_PLAYERS_TOTAL_ONLINE" /></span>
                            <span class={styles.summaryValue}>{formatDuration(0, totalOnline())}</span>
                        </div>
                        <div class={styles.summaryItem}>
                            <span class={styles.summaryLabel}><Text val="ONLINE_PLAYERS_SESSIONS_COUNT" /></span>
                            <span class={styles.summaryValue}>{history()!.sessions.length}</span>
                        </div>
                    </div>

                    <div class="fcard v" style={{ gap: "var(--spacing-md)" }}>
                        <h2 style={{ margin: 0 }}><Text val="ONLINE_PLAYERS_TIMELINE" /></h2>
                        <Show when={timeline().length > 0} fallback={
                            <div class={styles.empty}>
                                <i class="fa-solid fa-clock-rotate-left" />
                                <p><Text val="ONLINE_PLAYERS_HISTORY_EMPTY" /></p>
                            </div>
                        }>
                            <div class={styles.timeline}>
                                <For each={timeline()}>
                                    {(entry) => (
                                        <Show when={entry.kind === "session"} fallback={
                                            <div class={`${styles.entry} ${styles.entryGap}`}>
                                                <span class={`${styles.entryPill} ${styles.entryPillGap}`}>
                                                    <Text val="ONLINE_PLAYERS_NO_DATA" />
                                                </span>
                                                <div class={styles.entryTimes}>
                                                    <span class={styles.entryFrom}>{formatAbs((entry as GapEntry).from)}</span>
                                                    <span class={styles.entryArrow}>to</span>
                                                    <span class={styles.entryTo}>{formatAbs((entry as GapEntry).to)}</span>
                                                </div>
                                                <span class={styles.entryDuration}>
                                                    {formatDuration((entry as GapEntry).from, (entry as GapEntry).to)}
                                                </span>
                                            </div>
                                        }>
                                            <div class={`${styles.entry} ${styles.entrySession}`}>
                                                <span class={`${styles.entryPill} ${(entry as SessionEntry).offlineAt == null ? styles.entryPillOpen : styles.entryPillClosed}`}>
                                                    {(entry as SessionEntry).offlineAt == null ? "OPEN" : "ENDED"}
                                                </span>
                                                <div class={styles.entryTimes}>
                                                    <span class={styles.entryFrom}>{formatAbs((entry as SessionEntry).onlineAt)}</span>
                                                    <span class={styles.entryArrow}>to</span>
                                                    <span class={styles.entryTo}>
                                                        {(entry as SessionEntry).offlineAt == null ? "-" : formatAbs((entry as SessionEntry).offlineAt!)}
                                                    </span>
                                                </div>
                                                <span class={styles.entryDuration}>
                                                    {formatDuration((entry as SessionEntry).onlineAt, (entry as SessionEntry).offlineAt)}
                                                </span>
                                            </div>
                                        </Show>
                                    )}
                                </For>
                            </div>
                        </Show>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
